// src/components/Vision.jsx
import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";

// Animation variants (same style as About)
const sectionVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const cardsWrapperVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.18 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 35 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const Vision = ({ data }) => {
  return (
    <motion.section
      id="vision"
      style={{
        paddingTop: "90px",
        paddingBottom: "40px",
        background: "linear-gradient(180deg, #f9fdfd, #e6f7f7)",
      }}
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
    >
      <Container style={{ maxWidth: "1100px" }} className="px-3">
        
        {/* TITLE + INTRO */}
        <Row className="mb-3">
          <Col xs={12}>
            <motion.h1
              style={{
                fontSize: "clamp(2.3rem, 4vw, 2.9rem)",
                fontWeight: "800",
                color: "#054E48",
                marginBottom: "14px",
                textAlign: "center",
              }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Our Vision
            </motion.h1>

            <motion.p
              style={{
                fontSize: "clamp(1.1rem, 2.3vw, 1.35rem)",
                lineHeight: "2rem",
                color: "rgba(0,0,0,0.75)",
                maxWidth: "840px",
                margin: "0 auto",
                textAlign: "center",
              }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {data?.intro ||
                "At Sari-sari Web Development, our vision is to make modern, effective websites accessible to small businesses and creators—without the overwhelm. We want every sari-sari-level idea to have a world-class online presence."}
            </motion.p>
          </Col>
        </Row>

        {/* TWO CARDS WITH ANIMATION */}
        <motion.div
          variants={cardsWrapperVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <Row className="mt-3 gy-4">

            {/* FUTURE WE SEE (white) */}
            <Col xs={12} md={6}>
              <motion.div
                variants={cardVariants}
                whileHover={{
                  y: -6,
                  boxShadow: "0 14px 30px rgba(0,0,0,0.15)",
                }}
                transition={{ type: "spring", stiffness: 220, damping: 18 }}
                style={{
                  background: "#ffffff",
                  borderRadius: "16px",
                  padding: "22px 24px",
                  border: "1px solid rgba(0,0,0,0.06)",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                  height: "100%",
                }}
              >
                <h2
                  style={{
                    fontSize: "clamp(1.45rem, 3vw, 2rem)",
                    fontWeight: "700",
                    color: "#054E48",
                    marginBottom: "12px",
                  }}
                >
                  The future we see
                </h2>

                <p
                  style={{
                    fontSize: "clamp(1.05rem, 2.1vw, 1.2rem)",
                    lineHeight: "1.9rem",
                    color: "rgba(0,0,0,0.75)",
                    marginBottom: "10px",
                  }}
                >
                  {data?.future ||
                    "A web where even the smallest sari-sari store, solo freelancer, or starting brand can look professional online, tell their story clearly, and be discovered by the right people."}
                </p>

                <ul
                  style={{
                    fontSize: "clamp(1rem, 2.1vw, 1.15rem)",
                    lineHeight: "1.9rem",
                    color: "rgba(0,0,0,0.85)",
                    paddingLeft: "1.1rem",
                    marginBottom: 0,
                  }}
                >
                  <li>Websites that feel simple, not scary</li>
                  <li>Tools that match real everyday needs</li>
                  <li>Design that respects both brand and budget</li>
                  <li>Digital presence that grows with the business</li>
                </ul>
              </motion.div>
            </Col>

            {/* HOW WE GET THERE (teal) */}
            <Col xs={12} md={6}>
              <motion.div
                variants={cardVariants}
                whileHover={{
                  y: -6,
                  boxShadow: "0 14px 30px rgba(0,0,0,0.15)",
                }}
                transition={{ type: "spring", stiffness: 220, damping: 18 }}
                style={{
                  background: "rgba(21, 207, 192, 0.12)",
                  borderRadius: "16px",
                  padding: "22px 24px",
                  border: "1px solid rgba(5, 78, 72, 0.25)",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                  height: "100%",
                }}
              >
                <h2
                  style={{
                    fontSize: "clamp(1.45rem, 3vw, 2rem)",
                    fontWeight: "700",
                    color: "#054E48",
                    marginBottom: "12px",
                  }}
                >
                  How we get there
                </h2>

                <p
                  style={{
                    fontSize: "clamp(1.05rem, 2.1vw, 1.2rem)",
                    lineHeight: "1.9rem",
                    color: "rgba(0,0,0,0.75)",
                    marginBottom: "10px",
                  }}
                >
                  {data?.how ||
                    "We focus on small, clear, and repeatable building blocks: reusable components, clean layouts, and guided flows that make both the build process and user journey smoother."}
                </p>

                <p
                  style={{
                    fontSize: "clamp(1.05rem, 2.1vw, 1.2rem)",
                    lineHeight: "1.9rem",
                    color: "rgba(0,0,0,0.75)",
                    marginBottom: 0,
                  }}
                >
                  {data?.how2 ||
                    "One project at a time, we want to prove that great web experiences don’t have to be complicated or expensive—they just have to be thoughtful, intentional, and built for real people."}
                </p>
              </motion.div>
            </Col>
            
          </Row>
        </motion.div>
      </Container>
    </motion.section>
  );
};

export default Vision;
